import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import QuoteForm from "@/components/QuoteForm";
import SEO from "@/components/SEO";
import { useState } from "react";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const WindowGlassPage = () => {
  const [quoteOpen, setQuoteOpen] = useState(false);

  return (
    <div className="pt-20">
      <SEO
        title="Window Glass Replacement"
        description="Professional window glass replacement in coastal NC. We replace foggy, broken, or failed insulated and single-pane glass without full window replacement. Cost-effective solutions."
        keywords="window glass replacement, foggy window repair, insulated glass replacement, double pane glass, window repair NC, Brunswick County window glass"
        ogImage="https://images.unsplash.com/photo-1771371097061-3befd4b71b59?w=1200&q=80"
      />
      
      {/* Hero Section with Background Image */}
      <section className="relative min-h-[60vh] flex items-center bg-[#F9F9F7]">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1771371097061-3befd4b71b59?w=1920&q=80"
            alt="Modern home with large windows"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="section-container relative z-10 py-20">
          <Link to="/" className="inline-flex items-center gap-2 text-[#4B5563] font-body mb-6 hover:text-[#0F4C81] transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.span
              variants={fadeInUp}
              className="inline-block font-accent text-sm font-semibold tracking-widest uppercase text-[#0F4C81] mb-4"
            >
              Residential Services
            </motion.span>
            <motion.h1
              variants={fadeInUp}
              className="font-heading text-5xl md:text-6xl font-medium tracking-tight leading-[1.1] text-[#1A202C] mb-6"
            >
              Window Glass Replacement
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="font-body text-xl leading-relaxed text-[#4B5563] mb-8"
            >
              Restore clarity and efficiency to your windows. We replace foggy, broken, or failed glass without replacing the entire window—saving you time and money.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#0F4C81] text-white hover:bg-[#0F4C81]/90 rounded-full px-8 h-14 font-body font-semibold">
                    Get a Free Quote
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px] p-8">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl text-[#1A202C]">Request a Free Quote</DialogTitle>
                  </DialogHeader>
                  <QuoteForm onClose={() => setQuoteOpen(false)} />
                </DialogContent>
              </Dialog>
              <a href="tel:910-457-4757">
                <Button variant="outline" className="border-2 border-[#0F4C81] text-[#0F4C81] hover:bg-[#0F4C81] hover:text-white rounded-full px-8 h-14 font-body font-semibold">
                  <Phone className="w-5 h-5 mr-2" /> 910-457-4757
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-spacing bg-white">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80"
                alt="Window glass replacement"
                className="w-full h-[400px] object-cover"
              />
            </motion.div>
            
            {/* Content */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.h2 variants={fadeInUp} className="font-heading text-3xl font-medium text-[#1A202C] mb-6">
                What We Replace
              </motion.h2>
              <motion.ul variants={fadeInUp} className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-[#0F4C81] mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-body font-medium text-[#1A202C]">Insulated (Double-Pane) Glass</span>
                    <p className="font-body text-[#4B5563] text-sm mt-1">Failed seals causing fog or condensation between panes</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-[#0F4C81] mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-body font-medium text-[#1A202C]">Single-Pane Glass</span>
                    <p className="font-body text-[#4B5563] text-sm mt-1">Cracked, broken, or damaged single-pane windows</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-[#0F4C81] mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-body font-medium text-[#1A202C]">Fogged Windows</span>
                    <p className="font-body text-[#4B5563] text-sm mt-1">Restore clarity without replacing the entire window</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-[#0F4C81] mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-body font-medium text-[#1A202C]">All Window Styles</span>
                    <p className="font-body text-[#4B5563] text-sm mt-1">Casement, double-hung, picture, and specialty windows</p>
                  </div>
                </li>
              </motion.ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-spacing bg-[#F9F9F7]">
        <div className="section-container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.h2 variants={fadeInUp} className="font-heading text-4xl font-medium text-[#1A202C] mb-4">
              Benefits of Glass-Only Replacement
            </motion.h2>
            <motion.p variants={fadeInUp} className="font-body text-lg text-[#4B5563] max-w-2xl mx-auto">
              Save money and time by replacing just the glass instead of the entire window.
            </motion.p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white p-6 text-center"
            >
              <div className="font-heading text-4xl font-medium text-[#0F4C81] mb-2">50%</div>
              <div className="font-body text-sm text-[#4B5563]">Cost Savings vs Full Replacement</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white p-6 text-center"
            >
              <div className="font-heading text-4xl font-medium text-[#0F4C81] mb-2">Same Day</div>
              <div className="font-body text-sm text-[#4B5563]">Most Jobs Completed</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white p-6 text-center"
            >
              <div className="font-heading text-4xl font-medium text-[#0F4C81] mb-2">Energy</div>
              <div className="font-body text-sm text-[#4B5563]">Restored Efficiency</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-white p-6 text-center"
            >
              <div className="font-heading text-4xl font-medium text-[#0F4C81] mb-2">Clear</div>
              <div className="font-body text-sm text-[#4B5563]">Crystal Clear Views</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing bg-[#0F4C81]">
        <div className="section-container text-center">
          <h2 className="font-heading text-4xl font-medium text-white mb-4">Restore Clarity to Your Windows</h2>
          <p className="font-body text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Don't let foggy or broken windows affect your home's comfort and appearance. Contact us for a free assessment.
          </p>
          <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
            <DialogTrigger asChild>
              <Button className="bg-white text-[#0F4C81] hover:bg-white/90 rounded-full px-8 h-14 font-body font-semibold">
                Get Your Free Quote
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px] p-8">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-[#1A202C]">Request a Free Quote</DialogTitle>
              </DialogHeader>
              <QuoteForm onClose={() => setQuoteOpen(false)} />
            </DialogContent>
          </Dialog>
        </div>
      </section>
    </div>
  );
};

export default WindowGlassPage;
